
import { Question } from '../types';

export const fiqh101Questions: Question[] = [
  {
    id: 'fiqh001',
    text: 'What are the three linguistic definitions of Fiqh (فِقْه) as mentioned in the course?',
    type: 'multiple',
    options: [
      { id: 'a', text: 'To open something', isCorrect: true },
      { id: 'b', text: 'To learn something', isCorrect: true },
      { id: 'c', text: 'To have a deep understanding of something', isCorrect: true },
      { id: 'd', text: 'To close something', isCorrect: false },
      { id: 'e', text: 'To follow blindly', isCorrect: false }
    ],
    explanation: 'Fiqh (فِقْه) linguistically means to open something, to learn something, and to have a deep understanding of something. It was not designated to Islamic Law until decades after the time of the Prophet (ﷺ).',
    topic: 'fiqh101'
  },
  {
    id: 'fiqh002',
    text: 'What is the technical definition (iṣṭilāḥan) of Fiqh?',
    type: 'single',
    options: [
      { id: 'a', text: 'The study of Islamic history', isCorrect: false },
      { id: 'b', text: 'The knowledge of Islamic legal rulings derived from their detailed evidence', isCorrect: true },
      { id: 'c', text: 'The study of Qur\'anic recitation', isCorrect: false },
      { id: 'd', text: 'The science of hadith narration', isCorrect: false }
    ],
    explanation: 'The technical definition of Fiqh is "the knowledge of Islamic legal rulings (aḥkām) derived from their detailed evidence." In Arabic: هو العِلمُ بالأحْكام الشَّرْعِية الفَرْعِية مِنْ ادلّتِها التَّفْصِيلية',
    topic: 'fiqh101'
  },
  {
    id: 'fiqh003',
    text: 'What is the difference between Sharīʿah and Fiqh according to the course?',
    type: 'multiple',
    options: [
      { id: 'a', text: 'Sharīʿah is a direct deduction from the Qur\'ān and Sunnah, while Fiqh is deduced from Sharīʿah', isCorrect: true },
      { id: 'b', text: 'Sharīʿah is fixed and unchangeable, while Fiqh changes depending on circumstances', isCorrect: true },
      { id: 'c', text: 'Sharīʿah is more general and provides basic principles, while Fiqh is specific', isCorrect: true },
      { id: 'd', text: 'Sharīʿah applies only to Muslims, while Fiqh applies to everyone', isCorrect: false }
    ],
    explanation: 'Sharīʿah is a body of reviewed laws found directly in the Qur\'ān and Sunnah; it is fixed, unchangeable, and provides general principles. Fiqh is a body of laws further deduced from Sharīʿah to cover specific situations; it changes depending on circumstances and demonstrates how to apply Sharīʿah principles in given contexts.',
    topic: 'fiqh101'
  },
  {
    id: 'fiqh004',
    text: 'What are the four main sources of Fiqh as outlined in the course?',
    type: 'multiple',
    options: [
      { id: 'a', text: 'The Qur\'ān', isCorrect: true },
      { id: 'b', text: 'The Sunnah of the Prophet (ﷺ)', isCorrect: true },
      { id: 'c', text: 'Consensus (Ijmāʿ)', isCorrect: true },
      { id: 'd', text: 'Analogical reasoning (Qiyās)', isCorrect: true },
      { id: 'e', text: 'Personal opinion (Ra\'y)', isCorrect: false }
    ],
    explanation: 'The four main sources of Fiqh are: (1) The Qur\'ān, (2) The Sunnah of the Prophet Muhammad (ﷺ), (3) Ijmāʿ (consensus), and (4) Qiyās (analogical reasoning). Other secondary sources mentioned in the course include Sharʿu man qablanā (rulings for those who came before) and ʿUrf (customary practices).',
    topic: 'fiqh101'
  },
  {
    id: 'fiqh005',
    text: 'What is the subject matter of Fiqh according to the course?',
    type: 'single',
    options: [
      { id: 'a', text: 'The beliefs and creed of Muslims', isCorrect: false },
      { id: 'b', text: 'The actions and statements of a legally responsible individual (al-insān al-mukallaf)', isCorrect: true },
      { id: 'c', text: 'The history of Islamic civilization', isCorrect: false },
      { id: 'd', text: 'The interpretation of dreams', isCorrect: false }
    ],
    explanation: 'The subject matter of Fiqh is the actions and statements (and sometimes intentions) of a legally responsible individual (al-insān al-mukallaf). Fiqh can apply to both Muslims and in some cases non-Muslims.',
    topic: 'fiqh101'
  },
  {
    id: 'fiqh006',
    text: 'What conditions must be met for a person to be considered legally responsible (mukallaf) in Islam?',
    type: 'multiple',
    options: [
      { id: 'a', text: 'Being Muslim (more frequently, though some laws apply to non-Muslims)', isCorrect: true },
      { id: 'b', text: 'Being sane or mentally present', isCorrect: true },
      { id: 'c', text: 'Being human', isCorrect: true },
      { id: 'd', text: 'Having reached puberty (bulūgh)', isCorrect: true },
      { id: 'e', text: 'Being wealthy', isCorrect: false }
    ],
    explanation: 'For someone to be considered legally responsible (mukallaf) in Islam, they must be: (1) Muslim (more frequently, though some Islamic laws apply to non-Muslims), (2) Sane or mentally present, (3) Human, and (4) Having reached puberty (bulūgh). In cases where these conditions are not met, a person may have partial or no legal responsibility.',
    topic: 'fiqh101'
  },
  {
    id: 'fiqh007',
    text: 'What are the benefits of studying Fiqh as outlined in the course?',
    type: 'multiple',
    options: [
      { id: 'a', text: 'Guidance in worship', isCorrect: true },
      { id: 'b', text: 'Regulation of daily life', isCorrect: true },
      { id: 'c', text: 'Establishment of justice', isCorrect: true },
      { id: 'd', text: 'Moral and ethical conduct', isCorrect: true },
      { id: 'e', text: 'Adaptation to changing circumstances', isCorrect: true },
      { id: 'f', text: 'Increased social status', isCorrect: false }
    ],
    explanation: 'The benefits of studying Fiqh include: guidance in worship, regulation of daily life, establishment of justice, moral and ethical conduct, adaptation to changing circumstances, understanding the nuances of Islamic law, bridging the gap between theory and practice, navigating contemporary challenges, promoting intellectual growth, and fostering unity and informed discourse.',
    topic: 'fiqh101'
  },
  {
    id: 'fiqh008',
    text: 'What is Taqlīd as explained in the course?',
    type: 'single',
    options: [
      { id: 'a', text: 'The process of creating new Islamic rulings', isCorrect: false },
      { id: 'b', text: 'Following the scholarly opinions of a qualified jurist or madhab', isCorrect: true },
      { id: 'c', text: 'The compilation of hadith', isCorrect: false },
      { id: 'd', text: 'The process of memorizing the Qur\'an', isCorrect: false }
    ],
    explanation: 'Taqlīd linguistically means "a necklace to put around an animal\'s neck." Technically (iṣṭilāḥan), it means to follow someone or submit yourself to the research of another, particularly following the scholarly opinions of a qualified jurist or madhab (school of thought).',
    topic: 'fiqh101'
  },
  {
    id: 'fiqh009',
    text: 'What is Ijtihād as defined in the course?',
    type: 'single',
    options: [
      { id: 'a', text: 'Blind following of a scholar without understanding the evidence', isCorrect: false },
      { id: 'b', text: 'The effort made by a qualified scholar in seeking knowledge of Islamic rulings through interpretation', isCorrect: true },
      { id: 'c', text: 'The process of memorizing Islamic texts', isCorrect: false },
      { id: 'd', text: 'A form of worship', isCorrect: false }
    ],
    explanation: 'Ijtihād linguistically means "expending of maximum effort in the performance of an act." In Islamic terminology (iṣṭilāḥan), it refers to the effort made by a qualified scholar (mujtahid) in seeking knowledge of the rulings (aḥkām) of the Sharīʿah through interpretation of Islamic texts.',
    topic: 'fiqh101'
  },
  {
    id: 'fiqh010',
    text: 'What are the qualifications required for a person to be a mujtahid according to the course?',
    type: 'multiple',
    options: [
      { id: 'a', text: 'Deep knowledge of the Arabic language', isCorrect: true },
      { id: 'b', text: 'Knowledge and understanding of the Qur\'an', isCorrect: true },
      { id: 'c', text: 'Knowledge of the Sunnah of the Prophet Muhammad (ﷺ)', isCorrect: true },
      { id: 'd', text: 'Knowledge of Ijmāʿ (scholarly consensus)', isCorrect: true },
      { id: 'e', text: 'Understanding of Maqāṣid al-Sharīʿah (objectives of Islamic law)', isCorrect: true },
      { id: 'f', text: 'Aptitude and ability for ijtihād', isCorrect: true },
      { id: 'g', text: 'Wealth and social status', isCorrect: false }
    ],
    explanation: 'To be a mujtahid, one must have: deep knowledge of Arabic, thorough understanding of the Qur\'an and Sunnah, knowledge of scholarly consensus (ijmāʿ), understanding of the objectives of Sharīʿah (maqāṣid), and the aptitude and ability to perform ijtihād. This requires decades of study and mastery of various Islamic sciences.',
    topic: 'fiqh101'
  },
  {
    id: 'fiqh011',
    text: 'What is the difference between \'Ikhtilāf and Khilāf as explained in the course?',
    type: 'multiple',
    options: [
      { id: 'a', text: '\'Ikhtilāf is praiseworthy while Khilāf is blameworthy', isCorrect: true },
      { id: 'b', text: '\'Ikhtilāf has evidence (dalīl) to support it while Khilāf does not', isCorrect: true },
      { id: 'c', text: '\'Ikhtilāf brings mercy while Khilāf brings innovation (bidʿah)', isCorrect: true },
      { id: 'd', text: 'If a judge rules based on an \'Ikhtilāfi issue, it counts, but if based on a Khilāfi issue, it doesn\'t', isCorrect: true },
      { id: 'e', text: '\'Ikhtilāf is only found among non-Muslims while Khilāf is only found among Muslims', isCorrect: false }
    ],
    explanation: 'The differences between \'Ikhtilāf and Khilāf are: (1) \'Ikhtilāf is praiseworthy while Khilāf is blameworthy, (2) \'Ikhtilāf has evidence (dalīl) to support it while Khilāf does not, (3) \'Ikhtilāf brings mercy while Khilāf brings innovation (bidʿah), and (4) If a judge passes a verdict based on an \'Ikhtilāfi issue, it counts, but if it\'s based on a Khilāfi issue, it won\'t count.',
    topic: 'fiqh101'
  },
  {
    id: 'fiqh012',
    text: 'In which areas is \'Ikhtilāf (legitimate scholarly disagreement) found according to the course?',
    type: 'multiple',
    options: [
      { id: 'a', text: 'Fi al-\'adyān (among religions)', isCorrect: true },
      { id: 'b', text: 'Fi al-\'umūr al-\'aqā\'idīy (in matters of creed)', isCorrect: true },
      { id: 'c', text: 'Fi furū\' al-fiqhīyyah (in legal aspects of fiqh)', isCorrect: true },
      { id: 'd', text: 'Fi uṣūl al-dīn (in the fundamentals of religion)', isCorrect: false }
    ],
    explanation: '\'Ikhtilāf is found in three common areas: (1) Fi al-\'adyān (among religions, e.g., Islam, Judaism, Christianity), (2) Fi al-\'umūr al-\'aqā\'idīy (in matters of creed, but not major ones), and (3) Fi furū\' al-fiqhīyyah (in legal aspects of fiqh). The course emphasizes that \'Ikhtilāf in \'aqīdah is usually lafẓīy (verbal) rather than ḥaqīqīy (substantive).',
    topic: 'fiqh101'
  },
  {
    id: 'fiqh013',
    text: 'What are the reasons why \'Ikhtilāf (legitimate scholarly disagreement) exists according to the course?',
    type: 'multiple',
    options: [
      { id: 'a', text: 'Ṭabī\'ah \'uqūl al-mukallafīn (the nature of human intellect)', isCorrect: true },
      { id: 'b', text: 'Ṭabī\'ah al-nuṣṣūs al-taklīfīyyah (the nature of legislative texts)', isCorrect: true },
      { id: 'c', text: 'Ṭabī\'ah al-lughah al-\'arabīyyah (the nature of the Arabic language)', isCorrect: true },
      { id: 'd', text: 'Political disagreements among early scholars', isCorrect: false }
    ],
    explanation: 'The reasons why \'Ikhtilāf exists are: (1) Ṭabī\'ah \'uqūl al-mukallafīn (the nature of human intellect and how people process information differently), (2) Ṭabī\'ah al-nuṣṣūs al-taklīfīyyah (the nature of legislative texts, some having ambiguity as decreed by Allah), and (3) Ṭabī\'ah al-lughah al-\'arabīyyah (the nature of the Arabic language, which allows for various interpretations).',
    topic: 'fiqh101'
  },
  {
    id: 'fiqh014',
    text: 'What are the conditions for the validity of \'Ikhtilāf (legitimate scholarly disagreement) as discussed in the course?',
    type: 'multiple',
    options: [
      { id: 'a', text: 'The issue must be Mujtahad fihi (open to interpretation)', isCorrect: true },
      { id: 'b', text: 'The issue must not be Mujma\' \'alayh (subject to consensus)', isCorrect: true },
      { id: 'c', text: 'The person engaging in \'Ikhtilāf must have \'Ahlīyya (qualification)', isCorrect: true },
      { id: 'd', text: 'The person must have Ta\'ahhulu \'ilman (educational qualification)', isCorrect: true },
      { id: 'e', text: 'The person must have Ta\'ahhulu dīnan (faith qualification)', isCorrect: true },
      { id: 'f', text: 'The issue must be related to political matters', isCorrect: false }
    ],
    explanation: 'The conditions for the validity of \'Ikhtilāf are: (1) The issue must be Mujtahad fihi (open to interpretation) and not Mujma\' \'alayh (subject to consensus), (2) The person engaging in \'Ikhtilāf must have \'Ahlīyya (qualification), including both Ta\'ahhulu \'ilman (educational qualification) and Ta\'ahhulu dīnan (faith qualification).',
    topic: 'fiqh101'
  },
  {
    id: 'fiqh015',
    text: 'Which categories of Najāsah (impurity) are mentioned in the course?',
    type: 'multiple',
    options: [
      { id: 'a', text: 'Najāsah Ḥaqīqīyah (tangible/physical impurity)', isCorrect: true },
      { id: 'b', text: 'Najāsah Ḥukmīyyah (intangible/state of impurity)', isCorrect: true },
      { id: 'c', text: 'Najāsah Ghalīẓah (heavy impurity)', isCorrect: true },
      { id: 'd', text: 'Najāsah Khafīfah (light impurity)', isCorrect: true },
      { id: 'e', text: 'Najāsah Mutawassiṭah (moderate impurity)', isCorrect: false }
    ],
    explanation: 'The categories of Najāsah (impurity) include: (1) Najāsah Ḥaqīqīyah (tangible/physical impurity), which can be Ghalīẓah (heavy) or Khafīfah (light), and (2) Najāsah Ḥukmīyyah (intangible/state of impurity), which can be Aṣghar (smaller) requiring wuḍū\' or Akbar (larger) requiring ghusl.',
    topic: 'fiqh101'
  },
  {
    id: 'fiqh016',
    text: 'What are examples of Najāsah Ghalīẓah (heavy impurity) according to the course?',
    type: 'multiple',
    options: [
      { id: 'a', text: 'Blood', isCorrect: true },
      { id: 'b', text: 'Dog saliva', isCorrect: true },
      { id: 'c', text: 'Wine', isCorrect: true },
      { id: 'd', text: 'Urine', isCorrect: true },
      { id: 'e', text: 'Sweat', isCorrect: false }
    ],
    explanation: 'Examples of Najāsah Ghalīẓah (heavy impurity) include: blood, dog saliva, wine, pig saliva, stool, urine, vomit, alcohol, semen, and various forms of bodily discharge. By default, all physical, tangible impurities are considered Ghalīẓah unless specified otherwise.',
    topic: 'fiqh101'
  },
  {
    id: 'fiqh017',
    text: 'What are examples of Najāsah Khafīfah (light impurity) according to the course?',
    type: 'multiple',
    options: [
      { id: 'a', text: 'Urine of an animal whose meat is halal to eat', isCorrect: true },
      { id: 'b', text: 'Droppings of a bird', isCorrect: true },
      { id: 'c', text: 'Human blood', isCorrect: false },
      { id: 'd', text: 'Dog saliva', isCorrect: false }
    ],
    explanation: 'Examples of Najāsah Khafīfah (light impurity) include the urine of an animal whose meat is halal to eat (like a cow or chicken) and the droppings of a bird. This classification has implications for how much of the impurity can be present before washing is required.',
    topic: 'fiqh101'
  },
  {
    id: 'fiqh018',
    text: 'What is the difference between \'ibādāt and muʿāmalāt as explained in the course?',
    type: 'multiple',
    options: [
      { id: 'a', text: '\'Ibādāt are acts of worship directed towards Allah', isCorrect: true },
      { id: 'b', text: 'Muʿāmalāt involve actions and statements between creation', isCorrect: true },
      { id: 'c', text: 'In \'ibādāt, intentions matter more than in muʿāmalāt', isCorrect: true },
      { id: 'd', text: '\'Ibādāt can only be performed by Muslims while muʿāmalāt can apply to non-Muslims', isCorrect: true },
      { id: 'e', text: 'Muʿāmalāt are considered more important than \'ibādāt', isCorrect: false }
    ],
    explanation: 'The differences between \'ibādāt and muʿāmalāt are: (1) \'Ibādāt are acts of worship directed towards Allah (e.g., Zakāt, ṣalāh, Hajj), (2) Muʿāmalāt involve actions and statements between creation (e.g., how you speak with someone), (3) In \'ibādāt, intentions matter more than in muʿāmalāt, and (4) \'Ibādāt apply only to Muslims, while some muʿāmalāt can apply to non-Muslims.',
    topic: 'fiqh101'
  },
  {
    id: 'fiqh019',
    text: 'What is the difference between a Rasūl and a Nabī as explained in the course?',
    type: 'multiple',
    options: [
      { id: 'a', text: 'A Rasūl comes with a book or new revelation', isCorrect: true },
      { id: 'b', text: 'A Nabī does not come with a book/revelation', isCorrect: true },
      { id: 'c', text: 'There cannot be two Rasūls at one time in one place', isCorrect: true },
      { id: 'd', text: 'A Nabī\'s task is to spread, convey, and revive the meaning of existing revelation', isCorrect: true },
      { id: 'e', text: 'A Nabī is superior to a Rasūl', isCorrect: false }
    ],
    explanation: 'The differences between a Rasūl and a Nabī are: (1) A Rasūl comes with a book or new revelation, while a Nabī does not, (2) A Nabī\'s task is to spread, convey, and revive the meaning of existing revelation, (3) There cannot be two Rasūls at one time in one place, but there can be both a Rasūl and a Nabī at the same time (e.g., Mūsā was a Rasūl and his brother Hārūn was a Nabī).',
    topic: 'fiqh101'
  },
  {
    id: 'fiqh020',
    text: 'According to the course, what are the two aspects of every documented statement?',
    type: 'multiple',
    options: [
      { id: 'a', text: 'Lafẓ (the way the word is said, pronunciation, spelling, etc.)', isCorrect: true },
      { id: 'b', text: 'Ma\'nah (the meaning of the word)', isCorrect: true },
      { id: 'c', text: 'Ijtihād (interpretation)', isCorrect: false },
      { id: 'd', text: 'Ijmāʿ (consensus)', isCorrect: false }
    ],
    explanation: 'Every documented statement has two aspects: (1) Lafẓ (the way the word is said: the spelling, pronunciation, grammar, language, morphology, etc.) and (2) Ma\'nah (the meaning of the word - the specific manifestation of the contents). When people change the meaning while preserving the word, Allah would send a Nabī to revive the correct meaning.',
    topic: 'fiqh101'
  },
  {
    id: 'fiqh021',
    text: 'What historical eras are outlined in the course for the development and codification of Fiqh?',
    type: 'multiple',
    options: [
      { id: 'a', text: 'Time of the Prophet (ﷺ) and his Sahaba: The Foundation Era', isCorrect: true },
      { id: 'b', text: '2nd-3rd Century AH: The Formation of the Major Schools of Fiqh', isCorrect: true },
      { id: 'c', text: '3rd-4th Century AH: Codification and Expansion Era', isCorrect: true },
      { id: 'd', text: '5th-12th Century AH: Further Development', isCorrect: true },
      { id: 'e', text: '13th century onwards: The Modern Era of Fiqh', isCorrect: true },
      { id: 'f', text: 'Pre-Islamic era: The Formation Era', isCorrect: false }
    ],
    explanation: 'The historical eras outlined for the development of Fiqh are: (1) Time of the Prophet (ﷺ) and his Sahaba: The Foundation Era, (2) 2nd-3rd Century AH: The Formation of the Major Schools of Fiqh, (3) 3rd-4th Century AH: Codification and Expansion Era, (4) 5th-12th Century AH: Further Development (stress testing the system), and (5) 13th century onwards: The Modern Era of Fiqh (contemporary period).',
    topic: 'fiqh101'
  },
  {
    id: 'fiqh022',
    text: 'According to the course, who were the primary muftis among the Sahaba?',
    type: 'multiple',
    options: [
      { id: 'a', text: 'Ali ibn Abi Talib', isCorrect: true },
      { id: 'b', text: 'Zaid ibn Thabit', isCorrect: true },
      { id: 'c', text: 'Mu\'adh ibn Jabal', isCorrect: true },
      { id: 'd', text: 'Ibn Abbas', isCorrect: true },
      { id: 'e', text: 'Abdullah ibn Mas\'ūd', isCorrect: true },
      { id: 'f', text: 'Abu Lahab', isCorrect: false }
    ],
    explanation: 'The primary muftis among the Sahaba included: Ali ibn Abi Talib, Zaid ibn Thabit, Mu\'adh ibn Jabal, Ibn Abbas, and Abdullah ibn Mas\'ūd. The Prophet (ﷺ) specifically trained a group of the Sahaba in Islamic law to serve as references for fiqh issues.',
    topic: 'fiqh101'
  },
  {
    id: 'fiqh023',
    text: 'What are the types of Bayān (elaboration) in understanding Islamic texts as mentioned in the course?',
    type: 'multiple',
    options: [
      { id: 'a', text: 'Taqrīr (emphasis)', isCorrect: true },
      { id: 'b', text: 'Tafsīr (commentary)', isCorrect: true },
      { id: 'c', text: 'Taghyīr (exceptions)', isCorrect: true },
      { id: 'd', text: 'Tabdīl (making things contingent)', isCorrect: true },
      { id: 'e', text: 'Ḍarūrah (necessity)', isCorrect: true },
      { id: 'f', text: 'Taṣwīr (illustration)', isCorrect: false }
    ],
    explanation: 'The types of Bayān (elaboration) in understanding Islamic texts are: (1) Taqrīr (emphasis of something already evident), (2) Tafsīr (commentary providing further details), (3) Taghyīr (elaboration through exceptions), (4) Tabdīl (making things contingent upon one another), and (5) Ḍarūrah (elaboration by necessity).',
    topic: 'fiqh101'
  },
  {
    id: 'fiqh024',
    text: 'According to the course, what categories are texts classified into regarding clarity and authenticity?',
    type: 'multiple',
    options: [
      { id: 'a', text: 'Qaṭ\'ī uth-Thubūt (definitive in authenticity)', isCorrect: true },
      { id: 'b', text: 'Ẓannī uth-Thubūt (speculative in authenticity)', isCorrect: true },
      { id: 'c', text: 'Qaṭ\'ī ud-Dalālah (definitive in meaning)', isCorrect: true },
      { id: 'd', text: 'Ẓannī ud-Dalālah (speculative in meaning)', isCorrect: true },
      { id: 'e', text: 'Qaṭ\'ī ul-Lafẓ (definitive in wording)', isCorrect: false }
    ],
    explanation: 'Texts are categorized in terms of: (1) Thubūt (authenticity): Qaṭ\'ī (definitive) or Ẓannī (speculative), and (2) Dalālah (meaning/indication): Qaṭ\'ī (definitive) or Ẓannī (speculative). The entire Qur\'an is considered Qaṭ\'ī uth-Thubūt (definitive in authenticity), though many verses are Ẓannī ud-Dalālah (could have multiple meanings).',
    topic: 'fiqh101'
  },
  {
    id: 'fiqh025',
    text: 'What are the areas in which Taqlīd (following scholarly opinions) is applicable according to the course?',
    type: 'multiple',
    options: [
      { id: 'a', text: 'In matters of Fiqh (jurisprudence)', isCorrect: true },
      { id: 'b', text: 'In matters of \'Aqīdah (creed/belief)', isCorrect: false },
      { id: 'c', text: 'In matters of Tazkiyah (spiritual purification)', isCorrect: false }
    ],
    explanation: 'According to the course, Taqlīd is applicable in matters of Fiqh (jurisprudence) where the detailed understanding of evidence requires specialized knowledge. However, it is not appropriate in matters of \'Aqīdah (creed), where one should understand the basis of their belief, or in Tazkiyah (spiritual purification), where personal commitment rather than following is needed.',
    topic: 'fiqh101'
  },
  {
    id: 'fiqh026',
    text: 'What is the proper order for distributing the wealth of a deceased person according to the course?',
    type: 'single',
    options: [
      { id: 'a', text: 'Debts, burial costs, bequests, inheritance', isCorrect: false },
      { id: 'b', text: 'Burial costs, debts, bequests, inheritance', isCorrect: true },
      { id: 'c', text: 'Inheritance, bequests, debts, burial costs', isCorrect: false },
      { id: 'd', text: 'Bequests, burial costs, debts, inheritance', isCorrect: false }
    ],
    explanation: 'The proper order for distributing the wealth of a deceased person is: (1) Cost of burial, (2) Debts, (3) Waṣṣīyah (bequests, maximum 1/3 of remaining wealth), and (4) Wirāthah (inheritance according to Islamic law).',
    topic: 'fiqh101'
  },
  {
    id: 'fiqh027',
    text: 'According to the course, what is the ruling on paying the debts of a deceased person?',
    type: 'single',
    options: [
      { id: 'a', text: 'Heirs are legally obligated to pay off all debts of the deceased', isCorrect: false },
      { id: 'b', text: 'In Islam, no one inherits debts in the dunyā, but it is recommended for the heirs to pay them', isCorrect: true },
      { id: 'c', text: 'Debts are automatically forgiven upon death', isCorrect: false },
      { id: 'd', text: 'Only half of the debts need to be paid by the heirs', isCorrect: false }
    ],
    explanation: 'In Islam, no one inherits debts in the dunyā (worldly life). If someone dies with debts, the debt collectors legally must move on. However, in the ākhirah (afterlife), the deceased will be asked about those debts. The Prophet (ﷺ) would not lead the funeral prayer (janāzah) of someone who was in debt until the debts were cleared, indicating the importance of paying off debts.',
    topic: 'fiqh101'
  },
  {
    id: 'fiqh028',
    text: 'What did Shaykh Muhammad \'Awwama say about the existence of multiple madhabs in Islam according to the course?',
    type: 'single',
    options: [
      { id: 'a', text: 'Multiple madhabs are a source of division that should be eliminated', isCorrect: false },
      { id: 'b', text: 'Having four madhabs is a great blessing, as if Allah gave us four revelations at once', isCorrect: true },
      { id: 'c', text: 'Only one madhab should be followed by all Muslims', isCorrect: false },
      { id: 'd', text: 'Multiple madhabs exist because of political disagreements', isCorrect: false }
    ],
    explanation: 'Shaykh Muhammad \'Awwama said that the fact that we have four madhabs in our deen is such a great blessing; it\'s as if Allah gave us four revelations all at once. The fact that all four madhabs exist is a mercy to encompass all situations, providing flexibility in applying Islamic law.',
    topic: 'fiqh101'
  },
  {
    id: 'fiqh029',
    text: 'What is the concept of Talfīq as explained in the course?',
    type: 'single',
    options: [
      { id: 'a', text: 'Following different madhabs at different times on different issues', isCorrect: false },
      { id: 'b', text: 'Mixing between the opinions of different madhabs in a way that could make a practice invalid', isCorrect: true },
      { id: 'c', text: 'Creating a new madhab by combining elements of existing ones', isCorrect: false },
      { id: 'd', text: 'Rejecting all madhabs in favor of direct interpretation', isCorrect: false }
    ],
    explanation: 'Talfīq refers to "madhab hopping" or mixing between the opinions of different madhāhib in a way that could make a practice invalid. It can be harmful, but it is permissible in certain circumstances after careful consideration and going through a checklist to ensure it is jā\'iz (permissible).',
    topic: 'fiqh101'
  },
  {
    id: 'fiqh030',
    text: 'What is the purpose of Imam Bukhari\'s Sahih Bukhari according to the course?',
    type: 'single',
    options: [
      { id: 'a', text: 'Simply to collect authentic hadith', isCorrect: false },
      { id: 'b', text: 'To teach his students how to deduce rulings from hadith', isCorrect: true },
      { id: 'c', text: 'To establish a new madhab', isCorrect: false },
      { id: 'd', text: 'To refute other madhabs', isCorrect: false }
    ],
    explanation: 'According to the course, the actual purpose of Imam Bukhari\'s compilation of Sahih Bukhari was to teach his students how to deduce rulings from hadith - to teach them ijtihad, istinbat (figuring out specific rules from general teachings), and istikhraj (finding new rules through analysis). His book was a byproduct of this teaching effort.',
    topic: 'fiqh101'
  },
  {
    id: 'fiqh031',
    text: 'What is the principle of "Al aṣlu fil ashyā\'i ibāḥatun" as mentioned in the course?',
    type: 'single',
    options: [
      { id: 'a', text: 'All things are initially prohibited until proven permissible', isCorrect: false },
      { id: 'b', text: 'The default ruling in matters/things is permissibility', isCorrect: true },
      { id: 'c', text: 'All religious practices must have explicit textual evidence', isCorrect: false },
      { id: 'd', text: 'Matters of worship are by default permissible', isCorrect: false }
    ],
    explanation: 'The principle "Al aṣlu fil ashyā\'i ibāḥatun" means "the default ruling in matters/things is permissibility." However, the course notes that there are exceptions to this principle, including meat, sexual intercourse, and acts of worship to Allah, which are not permissible by default and need to be proven permissible through evidence.',
    topic: 'fiqh101'
  },
  {
    id: 'fiqh032',
    text: 'What is the ruling on studying Fiqh according to the course?',
    type: 'single',
    options: [
      { id: 'a', text: 'It is mandatory for everyone to become an expert in all areas of Fiqh', isCorrect: false },
      { id: 'b', text: 'It is mandatory to learn the rulings that allow a person to complete their religious obligations and do things in a permissible way', isCorrect: true },
      { id: 'c', text: 'It is completely optional and has no religious significance', isCorrect: false },
      { id: 'd', text: 'It is forbidden for common Muslims and only allowed for scholars', isCorrect: false }
    ],
    explanation: 'The ruling on studying Fiqh is that it is mandatory for Muslims to learn those rulings that allow them to complete their religious obligations and to do things in a permissible way. This includes understanding Ḥuqūq ul-Allah (rights of Allah) and Ḥuqūq ul-ʿibād (rights of people).',
    topic: 'fiqh101'
  },
  {
    id: 'fiqh033',
    text: 'What is meant by the statement "\'Ikhtilāf al-\'ummah raḥmah" (The disagreement of the ummah is a mercy)?',
    type: 'single',
    options: [
      { id: 'a', text: 'It is an authentic hadith from the Prophet (ﷺ)', isCorrect: false },
      { id: 'b', text: 'It is a logical conclusion scholars came to, not an authentic hadith', isCorrect: true },
      { id: 'c', text: 'It means all disagreements among Muslims are beneficial', isCorrect: false },
      { id: 'd', text: 'It is a modern invention with no historical basis', isCorrect: false }
    ],
    explanation: 'The statement "\'Ikhtilāf al-\'ummah raḥmah" (The disagreement of the ummah is a mercy) is not an authentic hadith attributed to the Prophet (ﷺ), but rather a logical conclusion that scholars came to based on their understanding of how scholarly differences of opinion provide flexibility and ease for the ummah.',
    topic: 'fiqh101'
  },
  {
    id: 'fiqh034',
    text: 'What is the difference between minor and major states of impurity (Najāsah Ḥukmīyyah) according to the course?',
    type: 'multiple',
    options: [
      { id: 'a', text: 'Minor impurity requires wudu to exit the state', isCorrect: true },
      { id: 'b', text: 'Major impurity requires ghusl to exit the state', isCorrect: true },
      { id: 'c', text: 'Minor impurity is caused by actions like passing gas or sleeping', isCorrect: true },
      { id: 'd', text: 'Major impurity is caused by events like sexual intercourse or menstruation', isCorrect: true },
      { id: 'e', text: 'Minor impurity prevents a person from entering a mosque', isCorrect: false }
    ],
    explanation: 'The differences between minor and major states of impurity (Najāsah Ḥukmīyyah): (1) Minor impurity (Aṣghar) requires wudu to exit the state and is caused by actions like passing gas, vomiting, sleeping, or using the bathroom. (2) Major impurity (Akbar) requires ghusl to exit the state and is caused by nocturnal emissions, intercourse, ejaculation, menstruation, or postpartum bleeding.',
    topic: 'fiqh101'
  },
  {
    id: 'fiqh035',
    text: 'According to the course, what is the wisdom behind the existence of multiple madhabs?',
    type: 'multiple',
    options: [
      { id: 'a', text: 'They provide flexibility in applying Islamic law to different circumstances', isCorrect: true },
      { id: 'b', text: 'They preserve the diverse interpretations of the Sahaba', isCorrect: true },
      { id: 'c', text: 'They allow for adaptation to different cultures and regions', isCorrect: true },
      { id: 'd', text: 'They create division among Muslims', isCorrect: false }
    ],
    explanation: 'The wisdom behind the existence of multiple madhabs includes: providing flexibility in applying Islamic law to different circumstances, preserving the diverse interpretations of the Sahaba, and allowing for adaptation to different cultures and regions. The course emphasizes that multiple madhabs are a mercy to the ummah, not a source of division.',
    topic: 'fiqh101'
  },
  {
    id: 'fiqh036',
    text: 'Who was the only imam among the four madhabs who was a tābi\'i (saw the Sahaba) according to the course?',
    type: 'single',
    options: [
      { id: 'a', text: 'Imam Malik', isCorrect: false },
      { id: 'b', text: 'Imam Shafi\'i', isCorrect: false },
      { id: 'c', text: 'Imam Ahmad ibn Hanbal', isCorrect: false },
      { id: 'd', text: 'Imam Abu Hanifa', isCorrect: true }
    ],
    explanation: 'According to the course, Imam Abu Hanifa was the only imam among the four madhabs who was a tābi\'i, meaning he met and saw companions of the Prophet (ﷺ). Specifically, he met Anas ibn Malik, who died around 93 AH when Imam Abu Hanifa was about 13 years old.',
    topic: 'fiqh101'
  },
  {
    id: 'fiqh037',
    text: 'What is the concept of "istihsan" as mentioned in the course?',
    type: 'single',
    options: [
      { id: 'a', text: 'Strict literal interpretation of texts', isCorrect: false },
      { id: 'b', text: 'Going against reasoning (khilāf al-qiyās) when strict application would lead to hardship', isCorrect: true },
      { id: 'c', text: 'Following the opinion of a specific sahabi', isCorrect: false },
      { id: 'd', text: 'Rejecting all forms of analogical reasoning', isCorrect: false }
    ],
    explanation: 'Istihsan refers to "juristic preference" and involves going against strict reasoning (khilāf al-qiyās) when its strict application would lead to hardship or an undesirable outcome. It\'s considered one of the weaker forms of ijtihād but can be necessary in certain circumstances.',
    topic: 'fiqh101'
  },
  {
    id: 'fiqh038',
    text: 'What is the concept of "maslaha" as mentioned in the course?',
    type: 'single',
    options: [
      { id: 'a', text: 'Strict adherence to literal meanings of texts', isCorrect: false },
      { id: 'b', text: 'Consideration of public interest or communal good', isCorrect: true },
      { id: 'c', text: 'Following the opinion of the majority', isCorrect: false },
      { id: 'd', text: 'Rejecting all modern interpretations', isCorrect: false }
    ],
    explanation: 'Maslaha refers to consideration of the public interest or communal good. It\'s one of the principles used in Islamic legal reasoning when direct texts don\'t address a specific issue. For example, the permissibility of voting might be justified based on maslaha (communal good) when there aren\'t explicit texts about it.',
    topic: 'fiqh101'
  },
  {
    id: 'fiqh039',
    text: 'What is the difference between Huqūq Allah and Huqūq al-\'ibād as mentioned in the course?',
    type: 'multiple',
    options: [
      { id: 'a', text: 'Huqūq Allah are rights of Allah (related to worship)', isCorrect: true },
      { id: 'b', text: 'Huqūq al-\'ibād are rights of people (related to interactions between humans)', isCorrect: true },
      { id: 'c', text: 'Intentions matter more in Huqūq Allah than in Huqūq al-\'ibād', isCorrect: true },
      { id: 'd', text: 'Huqūq Allah are less important than Huqūq al-\'ibād', isCorrect: false }
    ],
    explanation: 'Huqūq Allah refers to the rights of Allah, primarily related to acts of worship and obligations toward Allah. Huqūq al-\'ibād refers to the rights of people, related to interactions between humans. Intentions generally matter more in Huqūq Allah than in Huqūq al-\'ibād, where the impact on others is often more significant than the intention behind the act.',
    topic: 'fiqh101'
  },
  {
    id: 'fiqh040',
    text: 'What is the process of a mujtahid when deriving rulings according to the course?',
    type: 'multiple',
    options: [
      { id: 'a', text: 'First examine the Qur\'an and Sunnah for explicit rulings', isCorrect: true },
      { id: 'b', text: 'Study words requiring elaboration through proper bayān', isCorrect: true },
      { id: 'c', text: 'Check for abrogation (naskh) or whether texts are abrogated (mansukh)', isCorrect: true },
      { id: 'd', text: 'Apply principles of preference (tarjīḥ) when there are multiple arguments', isCorrect: true },
      { id: 'e', text: 'Rely primarily on personal opinion', isCorrect: false }
    ],
    explanation: 'The process of a mujtahid when deriving rulings includes: examining the Qur\'an and Sunnah for explicit rulings, studying words requiring elaboration through proper bayān, checking for abrogation (naskh) or whether texts are abrogated (mansukh), and applying principles of preference (tarjīḥ) when there are multiple arguments. The mujtahid must follow a methodical approach rather than relying on personal opinion.',
    topic: 'fiqh101'
  }
];
